import { Delete, Visibility, VisibilityOff } from '@mui/icons-material';
import { LinearProgress } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useTypedParams, useTypedSearchParams } from 'react-router-typesafe-routes/dom';
import { API } from '~/api';
import { AppRoutes } from '~/app/app.routes';
import { AuthService } from '~/service/service.auth';
import { PostCommentService } from '~/service/service.post.comment';
import { UIService } from '~/service/service.ui';
import { useService } from '~/shared/service/service.base';
import { ActionSetColumn, DateTimeColumn, useGridColumns } from '~/view/grid/grid.columns';
import { GridContainer } from '~/view/grid/grid.container';
import { GridNoRows } from '~/view/grid/grid.norows';
import { MuiGridPagination } from '~/view/grid/grid.pagination';
import { GridToolbarView } from '~/view/grid/grid.toolbar';

export const PostEditCommentsPage = observer(() => {
	const [comments, ui, auth] = useService(PostCommentService, UIService, AuthService);
	const columns = useGridColumns<API.PostCommentItem>(() => [
		{ field: 'id', headerName: 'ID' },
		DateTimeColumn({ field: 'createdAt', headerName: 'Created at' }),
		{ field: 'userNickName', headerName: 'Author', minWidth: 200 },
		{ field: 'moderation', headerName: 'Moderation', width: 200 },
		{ field: 'lang', headerName: 'Language', width: 100 },
		{ field: 'text', headerName: 'Text', flex: 1 },
		ActionSetColumn<API.PostCommentItem>({
			actions: [
				{
					label: 'Hide',
					icon: VisibilityOff,
					visible: row => row.moderation !== 'HIDDEN',
					onClick: row => comments.hide(row.postId, row.id).done(comments.reload),
					disabled: () => !auth.can('postComment', 'UPDATE'),
				},
				{
					label: 'Approve',
					icon: Visibility,
					visible: row => row.moderation === 'HIDDEN',
					onClick: row => comments.approve(row.postId, row.id).done(comments.reload),
					disabled: () => !auth.can('postComment', 'UPDATE'),
				},
				{
					label: 'Delete',
					icon: Delete,
					onClick: row => ui.confirmDelete(() => comments.delete(row.postId, row.id).done(comments.reload)),
					disabled: () => !auth.can('postComment', 'DELETE'),
				},
			],
		}),
	]);

	ui.usePageTitle('Post - Comments');
	const { id } = useTypedParams(AppRoutes.PostEdit);
	const [{ commentId }] = useTypedSearchParams(AppRoutes.PostEditCommentList);
	useEffect(() => comments.loadAndSubscribe(id, commentId), [comments, id, commentId]);

	return (
		<GridContainer async={comments.async} height="calc(100vh - 160px)">
			<DataGrid
				initialState={comments.gridState.initial()}
				loading={comments.async.loading}
				rowCount={comments.totalCount}
				rows={comments.items.slice()}
				columns={columns}
				disableColumnFilter
				paginationMode="server"
				sortingMode="server"
				onStateChange={comments.gridState.onStateChange}
				slots={{
					loadingOverlay: LinearProgress,
					noRowsOverlay: () => <GridNoRows>No comments found</GridNoRows>,
					pagination: MuiGridPagination,
					toolbar: () => (
						<GridToolbarView
							onReload={comments.reload}
							gridState={comments.gridState}
							exportEnabled={auth.can('postComment', 'EXPORT')}
						/>
					),
				}}
			/>
		</GridContainer>
	);
});
